import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, User, Heart, ShoppingBag, Menu, X, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useCurrency } from "@/context/CurrencyContext";

const ANNOUNCEMENTS = [
  "Free shipping within Canada on orders over $150",
  "All international customers are responsible for paying their own Customs, Taxes & Duties. No returns or refunds will be accepted if refused.",
];

const NAV = [
  { label: "Solid Modal", slug: "solid-modal", tagline: "Timeless everyday elegance",
    image: "https://images.unsplash.com/photo-1708151729245-fa18bd75727e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwc29saWQlMjBoaWphYiUyMGZhc2hpb258ZW58MHx8fHwxNzg0MDY1MDM3fDA&ixlib=rb-4.1.0&q=85&w=500" },
  { label: "Print Modal", slug: "print-modal", tagline: "Artful patterns, refined",
    image: "https://images.unsplash.com/photo-1552874869-5c39ec9288dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHx3b21hbiUyMHByaW50ZWQlMjBoaWphYnxlbnwwfHx8fDE3ODQwNjUwNTJ8MA&ixlib=rb-4.1.0&q=85&w=500" },
  { label: "Chiffon", slug: "chiffon", tagline: "Featherlight, luminous drape",
    image: "https://images.unsplash.com/photo-1650462167170-4cba33f8a24a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHx3b21hbiUyMHNpbGslMjBoaWphYnxlbnwwfHx8fDE3ODQwNjUwNTJ8MA&ixlib=rb-4.1.0&q=85&w=500" },
  { label: "Accessories", slug: "accessories", tagline: "The finishing touch",
    image: "https://images.unsplash.com/photo-1576053139778-7e32f2ae3cfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { logo, categories, nav } = useSiteSettings();
  const { currency, setCurrency, supported } = useCurrency();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [annIdx, setAnnIdx] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setAnnIdx((i) => (i + 1) % ANNOUNCEMENTS.length), 5000);
    return () => clearInterval(t);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/collection/all?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  const navItems = (nav && nav.length ? nav : NAV);
  const FALLBACK_CAT_IMG = "https://images.unsplash.com/photo-1708151729245-fa18bd75727e?w=500";

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Announcement bar */}
      <div className="bg-[#560216] text-white text-[10px] sm:text-[11px] uppercase tracking-[0.15em] py-2.5 px-4 text-center font-medium overflow-hidden" data-testid="announcement-bar">
        <AnimatePresence mode="wait">
          <motion.p
            key={annIdx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45 }}
            className="max-w-4xl mx-auto leading-relaxed"
            data-testid="announcement-text"
          >
            {ANNOUNCEMENTS[annIdx]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Nav */}
      <div
        className={`backdrop-blur-2xl border-b transition-all duration-300 ${
          scrolled ? "bg-white/90 border-[#E5E5E5]" : "bg-white/70 border-transparent"
        }`}
        onMouseLeave={() => setHovered(null)}
      >
        <div className="container-lira flex items-center justify-between h-24">
          {/* Mobile hamburger */}
          <button className="md:hidden" aria-label="Menu" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open">
            <Menu strokeWidth={1} className="h-6 w-6" />
          </button>

          {/* Logo */}
          <Link to="/" className="md:flex-1 flex items-center" data-testid="brand-logo">
            <img src={logo || "/lira-logo.png"} alt="LIRA" className="h-16 md:h-20 w-auto object-contain" />
          </Link>

          {/* Center nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <div key={item.slug} onMouseEnter={() => setHovered(item.slug)}>
                <Link
                  to={`/collection/${item.slug}`}
                  className="nav-underline text-xs uppercase tracking-[0.2em] py-2"
                  data-testid={`nav-${item.slug}`}
                >
                  {item.label}
                </Link>
              </div>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-4 md:gap-6 md:flex-1 md:justify-end">
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              aria-label="Currency"
              className="text-xs uppercase tracking-[0.15em] bg-transparent outline-none cursor-pointer border border-[#E5E5E5] rounded-full px-2 py-1 hover:border-[#1A1A1A] transition-colors"
              data-testid="currency-switcher"
            >
              {(supported.length ? supported : [{ code: currency, symbol: "$" }]).map((s) => (
                <option key={s.code} value={s.code}>{s.symbol} {s.code}</option>
              ))}
            </select>
            <button aria-label="Search" onClick={() => setSearchOpen((s) => !s)} data-testid="search-toggle">
              <Search strokeWidth={1} className="h-5 w-5" />
            </button>
            <Link to="/account" aria-label="Account" className="hidden sm:block" data-testid="account-link">
              <User strokeWidth={1} className="h-5 w-5" />
            </Link>
            <Link to="/wishlist" aria-label="Wishlist" className="relative" data-testid="wishlist-link">
              <Heart strokeWidth={1} className="h-5 w-5" />
              {wishCount > 0 && (
                <span className="absolute -top-2 -right-2 h-4 w-4 grid place-items-center rounded-full bg-[#1A1A1A] text-white text-[9px]">
                  {wishCount}
                </span>
              )}
            </Link>
            <button aria-label="Cart" className="relative" onClick={() => setOpen(true)} data-testid="cart-open">
              <ShoppingBag strokeWidth={1} className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 h-4 w-4 grid place-items-center rounded-full bg-[#1A1A1A] text-white text-[9px]" data-testid="cart-count">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mega menu */}
        {hovered && (
          <div
            className="hidden md:block absolute left-0 right-0 bg-white border-b border-[#E5E5E5] shadow-sm"
            onMouseEnter={() => setHovered(hovered)}
          >
            <div className="container-lira py-10 grid grid-cols-4 gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.slug}
                  to={`/collection/${item.slug}`}
                  className={`group flex flex-col gap-3 transition-opacity duration-300 ${
                    hovered === item.slug ? "opacity-100" : "opacity-50 hover:opacity-100"
                  }`}
                  onClick={() => setHovered(null)}
                >
                  <div className="overflow-hidden aspect-[4/3] rounded-sm bg-beige">
                    <img src={categories?.[item.slug] || item.image || FALLBACK_CAT_IMG} alt={item.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-lg">{item.label}</span>
                    <ArrowRight strokeWidth={1} className="h-4 w-4 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300" />
                  </div>
                  <span className="eyebrow">{item.tagline}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Search bar */}
        {searchOpen && (
          <div className="border-t border-[#E5E5E5] bg-white">
            <form onSubmit={submitSearch} className="container-lira py-5 flex items-center gap-4">
              <Search strokeWidth={1} className="h-5 w-5 text-[#737373]" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search for hijabs, colours, fabrics…"
                className="flex-1 bg-transparent outline-none text-sm tracking-wide placeholder:text-[#737373]"
                data-testid="search-input"
              />
              <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search">
                <X strokeWidth={1} className="h-5 w-5" />
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Mobile drawer menu */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-4/5 max-w-sm bg-white p-8 flex flex-col gap-8">
            <div className="flex items-center justify-between">
              <img src="/lira-logo.png" alt="LIRA" className="h-12 w-auto object-contain" />
              <button aria-label="Close menu" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close">
                <X strokeWidth={1} className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.slug}
                  to={`/collection/${item.slug}`}
                  onClick={() => setMobileOpen(false)}
                  className="font-serif text-2xl"
                  data-testid={`mobile-nav-${item.slug}`}
                >
                  {item.label}
                </Link>
              ))}
              <Link to="/collection/all" onClick={() => setMobileOpen(false)} className="font-serif text-2xl">
                All Collections
              </Link>
            </nav>
            <div className="mt-auto flex flex-col gap-4 text-sm">
              <Link to="/account" onClick={() => setMobileOpen(false)} className="flex items-center gap-3">
                <User strokeWidth={1} className="h-5 w-5" /> Account
              </Link>
              <Link to="/wishlist" onClick={() => setMobileOpen(false)} className="flex items-center gap-3">
                <Heart strokeWidth={1} className="h-5 w-5" /> Wishlist
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
